import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { getCourseById, getCoursesForUser } from '@nsc/simple-lms/api/lms-queries.js';
import type { LMSCourse } from '@nsc/simple-lms/entities/LMSCourse.js';
import type { LMSCourseEnrollment } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { LMSEnrollmentStatus } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { clampScorePercent } from '@nsc/simple-lms/client/score-utils.js';
import { html } from 'lit';
import * as m from '../../localization/out/messages.js';
import { router } from '../startup.js';

const statusBadge = (status?: LMSEnrollmentStatus, lockedAt?: Date | null) => {
  if (lockedAt) {
    return html`<span class="badge badge-error badge-sm">${m.academy_status_locked()}</span>`;
  }
  switch (status) {
    case LMSEnrollmentStatus.PASSED:
      return html`<span class="badge badge-success badge-sm">${m.academy_status_passed()}</span>`;
    case LMSEnrollmentStatus.FAILED:
      return html`<span class="badge badge-error badge-sm">${m.academy_status_failed()}</span>`;
    case LMSEnrollmentStatus.IN_PROGRESS:
      return html`<span class="badge badge-warning badge-sm">${m.academy_status_inProgress()}</span>`;
    default:
      return html`<span class="badge badge-info badge-sm">${m.academy_status_assigned()}</span>`;
  }
};

interface EnrollmentWithCourse {
  enrollment: LMSCourseEnrollment;
  course: LMSCourse | null;
}

export const AcademyPage = stateful(() => {
  const user = useObservable(() => currentUser(), []);

  const {
    value: enrollmentsWithCourses,
    loading,
    error,
  } = useQuery<EnrollmentWithCourse[] | null>(() => {
    if (!user?._id) return Promise.resolve(null);
    return getCoursesForUser(user._id)({
      _id: 1,
      courseId: 1,
      userId: 1,
      status: 1,
      lockedAt: 1,
      scorePercent: 1,
      completedAt: 1,
      createdAt: 1,
      attempts: { passed: 1, completedAt: 1, scorePercent: 1 },
    }).then(async enrollments => {
      const results: EnrollmentWithCourse[] = [];
      for (const enrollment of enrollments) {
        const course = enrollment.courseId
          ? await getCourseById(enrollment.courseId)({ _id: 1, title: 1, description: 1, language: 1 })
          : null;
        results.push({ enrollment, course });
      }
      return results;
    });
  }, [user?._id]);

  if (loading || !user) {
    return html`
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    `;
  }

  if (error) {
    return html`
      <div class="px-4 py-5 max-w-2xl mx-auto">
        <p class="text-sm text-error">${m.error_generic()}: ${error.message}</p>
      </div>
    `;
  }

  return html`
    <div class="px-4 py-5 max-w-2xl mx-auto">
      <h1 class="text-2xl font-bold mb-1">${m.academy_title()}</h1>
      <p class="text-base-content/60 text-sm mb-5">${m.academy_subtitle()}</p>

      ${enrollmentsWithCourses
        ? enrollmentsWithCourses.length === 0
          ? html`
              <div class="card bg-base-200">
                <div class="card-body items-center text-center">
                  <span class="icon-[material-symbols--school-outline] text-4xl text-base-content/30"></span>
                  <p class="text-base-content/50">${m.academy_empty()}</p>
                </div>
              </div>
            `
          : html`
              <div class="flex flex-col gap-3">
                ${enrollmentsWithCourses.map(({ enrollment, course }) => {
                  const attemptScores = (enrollment.attempts ?? []).map(a => clampScorePercent(a.scorePercent));
                  const bestScore = attemptScores.length > 0 ? Math.max(...attemptScores) : 0;
                  return html`
                    <a
                      href="${router.link('courseDetail', { params: { courseId: enrollment.courseId } })}"
                      class="card bg-base-100 shadow-sm border border-base-300 hover:shadow-md transition-shadow rounded-xl"
                    >
                      <div class="card-body p-4 gap-3">
                        <div class="flex items-start justify-between gap-3">
                          <div class="flex-1 min-w-0">
                            <h3 class="font-semibold text-base truncate">
                              ${course?.title ?? m.course_not_assigned()}
                            </h3>
                            ${course?.description
                              ? html`<p class="text-sm text-base-content/60 mt-1 line-clamp-2">
                                  ${course.description}
                                </p>`
                              : ''}
                          </div>
                          <div class="flex flex-col items-end gap-1 shrink-0">
                            ${statusBadge(enrollment.status, enrollment.lockedAt)}
                            ${course?.language
                              ? html`<span class="text-xs text-base-content/40">${course.language}</span>`
                              : ''}
                          </div>
                        </div>
                        ${enrollment.scorePercent !== null && enrollment.scorePercent !== undefined
                          ? html`
                              <div>
                                <div class="flex items-center justify-between text-xs text-base-content/50 mb-1">
                                  <span>${m.academy_score()}</span>
                                  <span class="font-medium">${bestScore}%</span>
                                </div>
                                <progress
                                  class="progress ${enrollment.status === LMSEnrollmentStatus.PASSED
                                    ? 'progress-success'
                                    : 'progress-error'} w-full h-2"
                                  value="${bestScore}"
                                  max="100"
                                ></progress>
                              </div>
                            `
                          : ''}
                      </div>
                    </a>
                  `;
                })}
              </div>
            `
        : html`
            <div class="flex items-center justify-center h-full">
              <span class="loading loading-spinner loading-md text-primary"></span>
            </div>
          `}
    </div>
  `;
});
